import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { ErrorObject } from 'src/app/_models/error';
import { TokenStatus } from 'src/app/_models/token-status';
import { UserService } from 'src/app/_services/user.service';
import { MatIcon } from '@angular/material/icon';
import { ButtonComponent } from 'src/app/_components/buttons/button/button.component';
import { LoadingSpinnerComponent } from 'src/app/_components/loading-spinner/loading-spinner.component';
import { NgOptimizedImage } from '@angular/common';

@Component({
  selector: 'app-update-email-confirmation',
  imports: [
    ButtonComponent,
    RouterLink,
    MatIcon,
    LoadingSpinnerComponent,
    NgOptimizedImage
  ],
  templateUrl: './update-email-confirmation.component.html',
  styleUrl: './update-email-confirmation.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UpdateEmailConfirmationComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly userService = inject(UserService);
  private readonly destroyRef = inject(DestroyRef);

  readonly validationError = signal<ErrorObject | undefined>(undefined);

  readonly tokenStatus = signal(TokenStatus.Validating);
  readonly TokenStatusEnum = TokenStatus;

  token = signal<string | null>(null);
  userId = signal<number | null>(null);

  ngOnInit(): void {
    this.token.set(this.route.snapshot.queryParams['token'] ?? null);
    const rawId = this.route.snapshot.queryParams['userId'];
    const parsed = rawId != null ? Number(rawId) : null;
    this.userId.set(Number.isNaN(parsed) ? null : parsed);

    // Remove token from URL to prevent http referrer leak
    void this.router.navigate([], { relativeTo: this.route, replaceUrl: true });

    if (this.token() && this.userId()) {
      this.userService
        .updateEmailConfirmation(this.userId(), this.token())
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: () => {
            this.tokenStatus.set(TokenStatus.Valid);
          },
          error: (error: ErrorObject) => {
            this.tokenStatus.set(TokenStatus.Invalid);
            this.validationError.set(error);
          },
        });
    } else {
      this.tokenStatus.set(TokenStatus.Invalid);
      this.validationError.set({
        status: -1,
        message: 'Invalid token. Please retry the link from your confirmation email and if the issue persists, contact the Super User for your Organisation.',
        code: null,
      });
    }
  }
}
